/**
 * /admin/chat-reports — reported Registry Chat conversations.
 * Privacy boundary: transcripts are only readable AFTER a report.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeftIcon as ArrowLeft, FlagIcon as Flag, CaretDownIcon as CaretDown, CaretUpIcon as CaretUp } from "@phosphor-icons/react";

function ReportRow({ r, onResolved }) {
  const [open, setOpen] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (!open && !transcript) {
      try {
        const res = await api.get(`/admin/chat/reports/${r.id}/messages`);
        setTranscript(res.data);
      } catch (e) { toast.error(e?.response?.data?.detail || "Could not load transcript."); return; }
    }
    setOpen((v) => !v);
  };

  const resolve = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/chat/reports/${r.id}/resolve`, { action });
      toast.success(action === "dismissed" ? "Report dismissed." : "Marked as actioned.");
      onResolved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
    finally { setBusy(false); }
  };

  const names = (r.participants || []).map((p) => p.name).join(" ↔ ");
  const moderate = async (userId, name, action) => {
    const reason = window.prompt(`${action === "warn" ? "Warning" : "Restriction"} reason for ${name} (they'll be notified by inbox + email):`);
    if (!reason || reason.trim().length < 3) return;
    try {
      await api.post(`/admin/chat/users/${userId}/${action}`, { reason: reason.trim() });
      toast.success(`${name} ${action === "warn" ? "warned" : "chat-restricted"}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  return (
    <div className="border border-[#F0EAD6] rounded-xl p-4" data-testid={`chat-report-${r.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <strong className="text-[14px]">{names}</strong>
        <span className="ml-auto text-[11px] text-[#9A9A9A]">
          {r.last_message_at ? new Date(r.last_message_at).toLocaleString() : ""}
        </span>
      </div>
      <div className="text-[12px] text-[#6B6B6B] mt-1">Last: {r.last_message_preview}</div>
      {(r.reports || []).map((rep, i) => (
        <div key={i} className="text-[12px] text-[#C1272D] mt-1">⚑ {rep.reason}</div>
      ))}
      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        {(r.participants || []).map((p) => (
          <span key={p.id} className="inline-flex items-center gap-1 text-[10px] bg-[#FBF7EC] border border-[#F0EAD6] rounded-full px-2 py-0.5">
            {p.name}
            <button type="button" onClick={() => moderate(p.id, p.name, "warn")} className="text-[#8A6A00] hover:underline font-bold" data-testid={`chat-report-warn-${r.id}-${p.id}`}>warn</button>
            <button type="button" onClick={() => moderate(p.id, p.name, "restrict")} className="text-[#C1272D] hover:underline font-bold" data-testid={`chat-report-restrict-${r.id}-${p.id}`}>restrict</button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button type="button" onClick={toggle} className="text-[11px] text-crimson hover:text-ink inline-flex items-center gap-1" data-testid={`chat-report-toggle-${r.id}`}>
          {open ? <CaretUp size={11} /> : <CaretDown size={11} />} {open ? "Hide transcript" : "Read transcript"}
        </button>
        <span className="flex-1" />
        <Button size="sm" variant="outline" disabled={busy} onClick={() => resolve("dismissed")} className="rounded-full text-[11px] h-7" data-testid={`chat-report-dismiss-${r.id}`}>
          Dismiss
        </Button>
        <Button size="sm" disabled={busy} onClick={() => resolve("actioned")} className="rounded-full text-[11px] h-7 bg-[#C1272D] hover:bg-ink text-white" data-testid={`chat-report-action-${r.id}`}>
          Actioned
        </Button>
      </div>
      {open && transcript && (
        <div className="mt-3 bg-[#FBF7EC] border border-[#F0EAD6] rounded-lg p-3 space-y-2 max-h-80 overflow-y-auto">
          {(transcript.messages || []).map((m) => (
            <div key={m.id} className="text-[12px]">
              <strong>{transcript.participants?.[m.sender_id]?.name || "?"}:</strong>{" "}
              <span className="whitespace-pre-wrap">{m.body}</span>
              {(m.scam_warning || m.pii_warning) && <span className="text-[#C1272D] ml-1">⚠</span>}
              <span className="text-[10px] text-[#9A9A9A] ml-2">{new Date(m.created_at).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminChatReports() {
  const [reports, setReports] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/chat/reports").then((r) => setReports(r.data?.reports || [])).catch(() => setReports([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-chat-reports-page">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-[#6B6B6B] hover:text-ink text-sm mb-4">
          <ArrowLeft size={14} weight="bold" /> Back to admin
        </Link>
        <header className="mb-6 pb-5 border-b border-[#E5E5E5]">
          <h1 className="font-spectral text-3xl sm:text-4xl text-ink flex items-center gap-3">
            <Flag size={30} weight="duotone" className="text-[#C1272D]" /> Reported conversations
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-2 max-w-2xl">
            Registry Chat threads reported by a participant. Transcripts are
            readable only after a report — un-reported conversations stay private.
          </p>
        </header>
        {reports === null ? (
          <div className="h-20 bg-[#FBF7EC] rounded-xl animate-pulse" />
        ) : reports.length === 0 ? (
          <p className="text-[13px] text-[#9A9A9A]">No open reports. 🎉</p>
        ) : (
          <div className="space-y-3">
            {reports.map((r) => <ReportRow key={r.id} r={r} onResolved={load} />)}
          </div>
        )}
      </div>
    </AppShell>
  );
}
